import { PRESETS, PRESET_ORDER, SHADE_HOME, type HornId } from "./presets";

export const SETTINGS_KEY = "gloam.settings.v1";
export type VoiceMode = "placed" | "lip";
/** Air, mute, and the bore's own lip control (shade) saved for one bore. */
export type PresetFavorite = { effort: number; mute: number; shade: number };
export type PresetFavorites = Partial<Record<HornId, PresetFavorite>>;
/** Mono is one horn. Poly is a chord of up to four independent horns. */
export type PlayMode = "mono" | "poly";
export type HornSettings = {
  version: 1;
  presetId: HornId;
  voiceMode: VoiceMode;
  /** When true, overlapping notes slur. When false, every new note re-attacks. */
  legato: boolean;
  /** Missing or unknown values stay mono, which is today's one-note horn. */
  playMode: PlayMode;
  /**
   * Horn (−1) through today's lip (0) to bass (+1).
   * Missing values stay at 0 so an older save keeps the current lip.
   */
  character: number;
  /** Current bore's own control. Missing saves stay at SHADE_HOME, that bore's lip. */
  shade: number;
  effort: number;
  mute: number;
  volume: number;
  base: number;
  narrowStart: number;
  favorites: PresetFavorites;
};

/** Persist only controls, never live notes, MIDI identity, pedal, or playback state. */
export function validateSettings(value: unknown): HornSettings {
  const raw =
    value && typeof value === "object" && "version" in value && value.version === 1
      ? (value as Record<string, unknown>)
      : {};
  const presetId = PRESET_ORDER.includes(raw.presetId as HornId)
    ? (raw.presetId as HornId)
    : "velvet";
  // New players (no saved choice) get the self-oscillating lip. An explicit "placed" is kept.
  const voiceMode: VoiceMode = raw.voiceMode === "placed" ? "placed" : "lip";
  const legato = raw.legato === false ? false : true;
  const playMode: PlayMode = raw.playMode === "poly" ? "poly" : "mono";
  const unit = (n: unknown, fallback: number) =>
    typeof n === "number" && Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : fallback;
  const bipolar = (n: unknown, fallback: number) =>
    typeof n === "number" && Number.isFinite(n) ? Math.max(-1, Math.min(1, n)) : fallback;
  const choice = (n: unknown, values: number[]) =>
    typeof n === "number" && values.includes(n) ? n : 48;
  const favorites: PresetFavorites = {};
  const rawFavs =
    raw.favorites && typeof raw.favorites === "object" ? (raw.favorites as Record<string, unknown>) : null;
  if (rawFavs) {
    for (const id of PRESET_ORDER) {
      const entry = rawFavs[id];
      if (!entry || typeof entry !== "object") continue;
      const fav = entry as Record<string, unknown>;
      if (typeof fav.effort !== "number" || typeof fav.mute !== "number") continue;
      if (!Number.isFinite(fav.effort) || !Number.isFinite(fav.mute)) continue;
      favorites[id] = {
        effort: unit(fav.effort, 0),
        mute: unit(fav.mute, 0),
        // Older favorites have no shade. Home keeps the new lip instead of turning it off.
        shade: unit(fav.shade, SHADE_HOME),
      };
    }
  }
  return {
    version: 1,
    presetId,
    voiceMode,
    legato,
    playMode,
    character: bipolar(raw.character, 0),
    shade: unit(raw.shade, SHADE_HOME),
    effort: unit(raw.effort, PRESETS[presetId].homeEffort),
    mute: unit(raw.mute, PRESETS[presetId].homeMute),
    volume: unit(raw.volume, 0.8),
    base: choice(raw.base, [36, 48, 60]),
    narrowStart: choice(raw.narrowStart, [36, 48, 60, 72]),
    favorites,
  };
}

/** Air, mute, and shade a preset starts on: the saved favorite, or that bore's home. */
export function expressionForPreset(id: HornId, favorites: PresetFavorites | undefined) {
  const fav = favorites?.[id];
  const preset = PRESETS[id];
  if (fav) return { effort: fav.effort, mute: fav.mute, shade: fav.shade ?? SHADE_HOME };
  return { effort: preset.homeEffort, mute: preset.homeMute, shade: SHADE_HOME };
}
